import { useState, useEffect, useCallback, useRef } from 'react';
import { stylesRegistry, ShaderStyle, ShaderPreset, ThemeTokens, defaultTheme } from './stylesRegistry';

// ============================================
// STORAGE KEYS
// ============================================
const STORAGE_KEYS = {
  AUTOPLAY: 'musashi-theme-autoplay',
  DURATION: 'musashi-theme-duration',
  STYLE_INDEX: 'musashi-theme-style-index',
  PRESET_INDEX: 'musashi-theme-preset-index',
  ROUND_INDEX: 'musashi-theme-round-index',
} as const;

// ============================================
// DEFAULT VALUES
// ============================================
const DEFAULT_DURATION = 15; // seconds
const MIN_DURATION = 5;
const MAX_DURATION = 60;

// ============================================
// TYPES
// ============================================
export interface ThemeRotationState {
  // Current indices
  styleIndex: number;
  presetIndex: number;
  roundIndex: number;

  // Settings
  autoplay: boolean;
  duration: number; // seconds
  isPaused: boolean; // temporary pause (e.g., when menu is open)

  // Derived values
  currentStyle: ShaderStyle;
  currentPreset: ShaderPreset;
  currentTheme: ThemeTokens;

  // Actions
  setStyle: (index: number) => void;
  setPreset: (index: number) => void;
  setStyleAndPreset: (styleIndex: number, presetIndex: number) => void;
  toggleAutoplay: () => void;
  setAutoplay: (value: boolean) => void;
  setDuration: (seconds: number) => void;
  next: () => void;
  prev: () => void;
  pause: (source?: string) => void;
  resume: (source?: string) => void;

  // Helpers
  styles: ShaderStyle[];
  timeRemaining: number; // seconds until next switch
}

// ============================================
// LOCAL STORAGE HELPERS
// ============================================
const loadFromStorage = <T>(key: string, defaultValue: T, validator?: (value: T) => boolean): T => {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return defaultValue;
    const parsed = JSON.parse(stored) as T;
    if (validator && !validator(parsed)) return defaultValue;
    return parsed;
  } catch {
    return defaultValue;
  }
};

const saveToStorage = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

// ============================================
// HOOK IMPLEMENTATION
// ============================================
export const useThemeRotation = (): ThemeRotationState => {
  const totalStyles = stylesRegistry.length;

  // Initialize state from localStorage
  const [styleIndex, setStyleIndexState] = useState(() =>
    loadFromStorage(STORAGE_KEYS.STYLE_INDEX, 0, (v) => v >= 0 && v < totalStyles)
  );

  const [presetIndex, setPresetIndexState] = useState(() => {
    const storedStyleIndex = loadFromStorage(STORAGE_KEYS.STYLE_INDEX, 0, (v) => v >= 0 && v < totalStyles);
    const maxPreset = stylesRegistry[storedStyleIndex]?.presets.length ?? 1;
    return loadFromStorage(STORAGE_KEYS.PRESET_INDEX, 0, (v) => v >= 0 && v < maxPreset);
  });

  const [roundIndex, setRoundIndexState] = useState(() =>
    loadFromStorage(STORAGE_KEYS.ROUND_INDEX, 0, (v) => v >= 0)
  );

  const [autoplay, setAutoplayState] = useState(() => {
    // Respect prefers-reduced-motion
    if (typeof window !== 'undefined') {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return false;
    }
    return loadFromStorage(STORAGE_KEYS.AUTOPLAY, true);
  });

  const [duration, setDurationState] = useState(() =>
    loadFromStorage(STORAGE_KEYS.DURATION, DEFAULT_DURATION, (v) => v >= MIN_DURATION && v <= MAX_DURATION)
  );

  const [timeRemaining, setTimeRemaining] = useState(duration);
  const [pauseSources, setPauseSources] = useState<Set<string>>(new Set());
  const isPaused = pauseSources.size > 0;

  // Timer ref for cleanup
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Derived values
  const currentStyle = stylesRegistry[styleIndex] ?? stylesRegistry[0];
  const currentPreset = currentStyle.presets[presetIndex] ?? currentStyle.presets[0];
  const currentTheme = currentPreset?.theme ?? defaultTheme;

  // ============================================
  // ROTATION LOGIC
  // ============================================

  /**
   * Advance to next style/preset following the "fake more pages" pattern:
   * - Each tick advances to the next style
   * - When wrapping from last style to first, increment roundIndex
   * - Preset for each style is determined by roundIndex % presetCount
   */
  const advanceToNext = useCallback(() => {
    setStyleIndexState(prevStyleIndex => {
      const nextStyleIndex = (prevStyleIndex + 1) % totalStyles;
      const isWrapping = nextStyleIndex === 0;

      // Update round index when wrapping
      if (isWrapping) {
        setRoundIndexState(prevRound => {
          const newRound = prevRound + 1;
          saveToStorage(STORAGE_KEYS.ROUND_INDEX, newRound);
          return newRound;
        });
      }

      // Calculate preset for the new style based on current round
      // (We need to read roundIndex here, but since we might have just incremented it,
      // we need to account for that)
      const effectiveRound = isWrapping ? roundIndex + 1 : roundIndex;
      const nextStyle = stylesRegistry[nextStyleIndex];
      const nextPresetIndex = effectiveRound % nextStyle.presets.length;

      setPresetIndexState(nextPresetIndex);
      saveToStorage(STORAGE_KEYS.PRESET_INDEX, nextPresetIndex);
      saveToStorage(STORAGE_KEYS.STYLE_INDEX, nextStyleIndex);

      return nextStyleIndex;
    });

    // Reset timer
    setTimeRemaining(duration);
  }, [totalStyles, roundIndex, duration]);

  /**
   * Go to previous style/preset
   */
  const advanceToPrev = useCallback(() => {
    setStyleIndexState(prevStyleIndex => {
      const nextStyleIndex = prevStyleIndex === 0 ? totalStyles - 1 : prevStyleIndex - 1;
      const isWrappingBack = prevStyleIndex === 0;

      // Update round index when wrapping back
      if (isWrappingBack && roundIndex > 0) {
        setRoundIndexState(prevRound => {
          const newRound = Math.max(0, prevRound - 1);
          saveToStorage(STORAGE_KEYS.ROUND_INDEX, newRound);
          return newRound;
        });
      }

      // Calculate preset for the new style
      const effectiveRound = isWrappingBack ? Math.max(0, roundIndex - 1) : roundIndex;
      const nextStyle = stylesRegistry[nextStyleIndex];
      const nextPresetIndex = effectiveRound % nextStyle.presets.length;

      setPresetIndexState(nextPresetIndex);
      saveToStorage(STORAGE_KEYS.PRESET_INDEX, nextPresetIndex);
      saveToStorage(STORAGE_KEYS.STYLE_INDEX, nextStyleIndex);

      return nextStyleIndex;
    });

    // Reset timer
    setTimeRemaining(duration);
  }, [totalStyles, roundIndex, duration]);

  // ============================================
  // PUBLIC ACTIONS
  // ============================================

  const setStyle = useCallback((index: number) => {
    const validIndex = Math.max(0, Math.min(index, totalStyles - 1));
    setStyleIndexState(validIndex);
    saveToStorage(STORAGE_KEYS.STYLE_INDEX, validIndex);

    // Reset preset to match current round
    const style = stylesRegistry[validIndex];
    const newPresetIndex = roundIndex % style.presets.length;
    setPresetIndexState(newPresetIndex);
    saveToStorage(STORAGE_KEYS.PRESET_INDEX, newPresetIndex);

    // Reset timer
    setTimeRemaining(duration);
  }, [totalStyles, roundIndex, duration]);

  const setPreset = useCallback((index: number) => {
    const maxPreset = currentStyle.presets.length;
    const validIndex = Math.max(0, Math.min(index, maxPreset - 1));
    setPresetIndexState(validIndex);
    saveToStorage(STORAGE_KEYS.PRESET_INDEX, validIndex);

    // Reset timer
    setTimeRemaining(duration);
  }, [currentStyle.presets.length, duration]);

  const setStyleAndPreset = useCallback((newStyleIndex: number, newPresetIndex: number) => {
    const validStyleIndex = Math.max(0, Math.min(newStyleIndex, totalStyles - 1));
    const style = stylesRegistry[validStyleIndex];
    const validPresetIndex = Math.max(0, Math.min(newPresetIndex, style.presets.length - 1));

    setStyleIndexState(validStyleIndex);
    setPresetIndexState(validPresetIndex);
    saveToStorage(STORAGE_KEYS.STYLE_INDEX, validStyleIndex);
    saveToStorage(STORAGE_KEYS.PRESET_INDEX, validPresetIndex);

    // Reset timer
    setTimeRemaining(duration);
  }, [totalStyles, duration]);

  const toggleAutoplay = useCallback(() => {
    setAutoplayState(prev => {
      const newValue = !prev;
      saveToStorage(STORAGE_KEYS.AUTOPLAY, newValue);
      if (newValue) {
        setTimeRemaining(duration);
      }
      return newValue;
    });
  }, [duration]);

  const setAutoplay = useCallback((value: boolean) => {
    setAutoplayState(value);
    saveToStorage(STORAGE_KEYS.AUTOPLAY, value);
    if (value) {
      setTimeRemaining(duration);
    }
  }, [duration]);

  const setDuration = useCallback((seconds: number) => {
    const validDuration = Math.max(MIN_DURATION, Math.min(MAX_DURATION, seconds));
    setDurationState(validDuration);
    saveToStorage(STORAGE_KEYS.DURATION, validDuration);
    setTimeRemaining(validDuration);
  }, []);

  // Pause/resume functions for temporary pausing (e.g., when menu is open)
  const pause = useCallback((source: string = 'default') => {
    setPauseSources(prev => {
      const next = new Set(prev);
      next.add(source);
      return next;
    });
  }, []);

  const resume = useCallback((source: string = 'default') => {
    setPauseSources(prev => {
      if (!prev.has(source)) return prev;
      const next = new Set(prev);
      next.delete(source);
      return next;
    });
  }, []);

  // ============================================
  // AUTOPLAY TIMER
  // ============================================

  useEffect(() => {
    // Clear existing timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    // Don't run timers if autoplay is off or paused
    if (!autoplay || isPaused) return;

    // Set up main timer for advancing
    timerRef.current = setInterval(() => {
      advanceToNext();
    }, duration * 1000);

    // Set up countdown timer
    countdownRef.current = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [autoplay, isPaused, duration, advanceToNext]);

  // Reset time remaining when duration changes
  useEffect(() => {
    setTimeRemaining(duration);
  }, [duration]);

  return {
    styleIndex,
    presetIndex,
    roundIndex,
    autoplay,
    duration,
    isPaused,
    currentStyle,
    currentPreset,
    currentTheme,
    setStyle,
    setPreset,
    setStyleAndPreset,
    toggleAutoplay,
    setAutoplay,
    setDuration,
    next: advanceToNext,
    prev: advanceToPrev,
    pause,
    resume,
    styles: stylesRegistry,
    timeRemaining,
  };
};

export default useThemeRotation;
